import { inject, observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import BlockKitStore from "../stores/BlockKitStore";
import { AllCommunityModule, ICellRendererParams, ModuleRegistry } from 'ag-grid-community';
import { Container, Button, Stack, Badge } from "react-bootstrap";
import LayoutEditModal from "../components/LayoutEditModal/LayoutEditModal";
import { LayoutJsonModel } from "../models/BlockTransportSystemJsonModel";
import { AgGridReact } from "ag-grid-react";
import { useTranslation } from "react-i18next";

ModuleRegistry.registerModules([AllCommunityModule]);

export interface IProps {
  blockKitStore?: BlockKitStore;
}

const LayoutPage: React.FC<IProps> = inject("blockKitStore")(observer(({
  blockKitStore
}) => {
  const [isShowEdit, setIsShowEdit] = useState<boolean>(false);
  const [isShowCreate, setIsShowCreate] = useState<boolean>(false);
  const [selectedLayout, setSelectedLayout] = useState<LayoutJsonModel>();
  const { t } = useTranslation();

  useEffect(() => {
    blockKitStore?.initLayoutPageAsync();
  }, []);

  const EditButtonCell: React.FC<ICellRendererParams> = (params) => {
      return (
        <Button
          variant="secondary"
          size="sm"
          onClick={async () => {
              setSelectedLayout(params.data);
              setIsShowEdit(true);
              params.api.deselectAll();
              params.node.setSelected(true);
          }}>
          {t("button-edit")}
        </Button>
      );
    }
  return (
    <Container fluid>
      {isShowCreate &&
        <LayoutEditModal
          isShow={isShowCreate}
          onClickCreate={async (layout: LayoutJsonModel) => {
            await blockKitStore?.studioController.postLayoutsStagingAsync(layout);
            await blockKitStore?.initLayoutPageAsync();
            setIsShowCreate(false);
          }}
          onClickSave={async (layout: LayoutJsonModel) => {
            await blockKitStore?.studioController.putLayoutStagingAsync(layout);
            await blockKitStore?.initLayoutPageAsync();
            setIsShowCreate(false);
          }}
          onClickClose={() => {
            setIsShowCreate(false);
          }}
        />
      }
      {isShowEdit &&
        <LayoutEditModal
          isShow={isShowEdit}
          selectedLayout={selectedLayout}
          onClickCreate={async (layout: LayoutJsonModel) => {
            await blockKitStore?.studioController.postLayoutsStagingAsync(layout);
            await blockKitStore?.initLayoutPageAsync();
            setIsShowEdit(false);
          }}
          onClickSave={async (layout: LayoutJsonModel) => {
            await blockKitStore?.studioController.putLayoutStagingAsync(layout);
            await blockKitStore?.initLayoutPageAsync();
            setIsShowEdit(false);
          }}
          onClickClose={() => {
            setIsShowEdit(false);
          }}
        />
      }
      <Stack direction="horizontal" gap={1} className="m-2">
        <Button
          onClick={() => setIsShowCreate(true)}
        >
          {t("button-new")}
        </Button>
        <Button
          disabled={!selectedLayout}
          onClick={async () => {
            if (!confirm(t("publish-confirmation-message"))) {
              return;
            }

            const response = await blockKitStore?.studioController.publishLayoutsStagingAsync(selectedLayout);
            if (response?.isSuccess) {
              await blockKitStore?.initLayoutPageAsync();
              alert(t("publish-success"));
            } else {
              alert(`${t("publish-failed")} (${response?.message})`);
            }
          }}
        >
          {t("button-publish")}
        </Button>
      </Stack>
      <div style={{ height: "70vh" }}>
        <AgGridReact<LayoutJsonModel>
          getRowId={params => params.data.layoutId}
          rowData={blockKitStore?.layouts}
          rowSelection={{
            mode: "singleRow",
            enableClickSelection: false
          }}
          onSelectionChanged={(e) => {
            const selected = e.api.getSelectedRows();
            if (selected.length > 0) {
              setSelectedLayout(selected[0]);
            } else {
              setSelectedLayout(undefined);
            }
          }}
          columnDefs={[
            {
              headerName: t("publish"),
              width: 80,
              cellRenderer: (params: ICellRendererParams) => {
                return blockKitStore?.publishedLayouts.some(i => i.layoutId === params.data?.layoutId)
                ? <Badge bg="info">{t("published")}</Badge>
                : null
              },
            },
            {
              headerName: t("edit"),
              width: 80,
              cellRenderer: EditButtonCell,
            },
            {
              headerName: t("id"),
              filter: true,
              field: "layoutId"
            },
            {
              headerName: t("layout-type"),
              filter: true,
              field: "layoutType",
            },
            {
              headerName: t("block-type"),
              filter: true,
              field: "blockType",
            },
          ]}
          />
      </div>
    </Container>
  )
}));

export default LayoutPage;